<template>

    <div class="header">

        <div class="header__row" v-if="!searching || isDesktop">
            <div class="header__progress" v-if="isDesktop">
                <div class="header__progress__left">
                    <circle-progress-bar
                        :percent="percent"></circle-progress-bar>
                </div>
                <div class="header__progress__right">
                    <p class="text-lMedium color__moon">
                        <span class="text-lSemibold color__charcoal">{{ this.calcDone }}</span>
                        <span>{{ `/${this.calcAll}` }}</span>
                    </p>
                    <p class="text-lRegular color__moon">{{ $tc('cleaning_total_treated.treated_newsletter_formatted', this.calcDone) }}</p>
                </div>
            </div>

            <div class="header__mobile" v-show="!isDesktop">

                <div v-if="countSelectedNewsletters" class="header__mobile__selected"
                     @click="unselectedAllNewsletters">

                    <img src="/img/newsletters-v2/close.svg" class="img__20 mr__4">
                    <p>{{ computedSelected }}</p>

                </div>

                <button class="header__btn__mail" v-else @click="openBSMS">
                    <p class="text-lSemibold mr__8">{{ $t('cleaning_select_inbox') }}</p>
                    <img src="/img/newsletters-v2/chevron-down-blue.svg" class="img__24">
                </button>

            </div>

            <button class="header__btn__history" @click="goMyActions">
                <img src="/img/newsletters-v2/clock.svg" class="img__24 mr__8">
                <p class="text-lSemibold color__white">{{ $t('reverse_action') }}</p>
            </button>
        </div>

        <template v-if="!searching">
            <div class="header__row">
                <div class="header__choice">
                    <button class="header__btn__template text-lSemibold"
                            :class="{'header__btn__template__active': page === ENUM.NEWSLETTERS.PAGE.LIST}"
                            @click="updateNewslettersPage(ENUM.NEWSLETTERS.PAGE.LIST)">
                        {{ $t('cleaning_list') }}
                    </button>
                    <button class="header__btn__template text-lSemibold"
                            :class="{'header__btn__template__active': page === ENUM.NEWSLETTERS.PAGE.CARDS}"
                            @click="updateNewslettersPage(ENUM.NEWSLETTERS.PAGE.CARDS)">
                        {{ $t('cleaning_cards') }}
                    </button>
                </div>

                <header-categories v-show="isDesktop"
                                   :category="category"
                                   :subscriptions="subscriptions"
                                   @updateCategory="updateCategory"></header-categories>

                <div class="header__line">
                    <button class="header__btn__search mr__20" @click="search">
                        <img src="/img/newsletters-v2/search-grey.svg" class="img__24">
                        <p class="ml__12 text-lSemibold" v-show="isDesktop">{{ $t('cleaning_search_cta') }}</p>
                    </button>

                    <button class="header__btn__sort"
                            :class="{'header__btn__sort__active': newslettersSort !== ENUM.NEWSLETTERS.SORT.NOT_SET}"
                            @click="openSort">
                        <img src="/img/newsletters-v2/sort-ascending.svg" class="img__24">
                        <p class="text-lSemibold ml__8">{{ computedSortTitle }}</p>
                    </button>
                </div>
            </div>

            <header-categories v-show="!isDesktop && isList"
                               :category="category"
                               :subscriptions="subscriptions"
                               @updateCategory="updateCategory"></header-categories>

        </template>

        <div class="header__search" v-show="searching">

            <label class="header__search__label mr__16" for="searchInput">
                <img src="/img/newsletters-v2/search-grey.svg" class="img__24 mr__8">
                <input type="text"
                       id="searchInput"
                       ref="searchInput"
                       v-model="searchValue"
                       class="header__search__input text-lSemibold"
                       :placeholder="$t('cleaning_search_cta')">
                <img src="/img/newsletters-v2/x-blue.svg" class="img__24 ml__8 pointer"
                     @click="searching = false"
                     v-show="isDesktop">
            </label>
            <button class="text-lSemibold color__aquamarine ds__link__default"
                    @click="searching = false"
                    v-show="!isDesktop">
                {{ $t('cleaning_cancel_actions') }}
            </button>
        </div>

        <sort ref="SORT"></sort>

        <bottom-sheet-mailboxes-selection ref="BSMS"></bottom-sheet-mailboxes-selection>

    </div>

</template>

<script>
import Vuex from 'vuex'

import ENUM from '@/assets/ENUM'

import Storage from '@/helpers/storage'
import Tag from '@/modules/tag'

import BottomSheetMailboxesSelection from '@/components/bottomSheet/BottomSheetMailboxesSelection'
import CircleProgressBar from '@/components/newsletters-v2/CircleProgressBar'
import HeaderCategories from '@/components/newsletters-v2/HeaderCategories'
import Sort from '@/components/newsletters-v2/sort/Sort'

export default {

    name: 'newsletters-header',

    components: {
        BottomSheetMailboxesSelection,
        CircleProgressBar,
        HeaderCategories,
        Sort
    },

    props: {
        page: {
            type: String,
            default: ''
        },

        category: {
            type: String,
            default: ''
        },

        subscriptions: {
            type: Array,
            default: () => []
        },

        maxSubscriptions: {
            type: Number,
            default: 0
        }
    },

    watch: {
        searching: function (bool) {
            if (bool) {
                const input = this.$refs.searchInput
                setTimeout(function () {
                    input.focus()
                }, 100)
            } else {
                this.searchValue = ''
            }
            this.$emit('updateSearching', bool)
        },

        searchValue: function (newValue) {
            this.$emit('updateSearchValue', newValue)
        }
    },

    computed: {

        ...Vuex.mapGetters([
            'countSelectedNewsletters',
            'newslettersSort'
        ]),

        computedSelected() {
            return this.$tc('cleaning_list_selected', this.countSelectedNewsletters, {nbSelected: this.countSelectedNewsletters})
        },

        ENUM() {
            return ENUM
        },

        calcAll() {
            return this.maxSubscriptions || 0
        },

        calcDone() {
            const treated = this.calcAll - this.subscriptions.length || 0
            return treated >= 0 ? treated : 0
        },

        progress() {
            return `${this.calcDone}/${this.calcAll}`
        },

        percent() {
            return (100 * this.calcDone) / this.calcAll
        },

        computedSortTitle() {
            if (this.isDesktop) {
                switch (this.newslettersSort) {
                    case ENUM.NEWSLETTERS.SORT.MOST_OPEN:
                        return this.$t('sort_by_most_open')
                    case ENUM.NEWSLETTERS.SORT.LEAST_OPEN:
                        return this.$t('sort_by_less_open')
                    case ENUM.NEWSLETTERS.SORT.ASCENDING:
                        return this.$t('sort_by_ascending')
                    case ENUM.NEWSLETTERS.SORT.DESCENDING:
                        return this.$t('sort_by_descending')
                    default:
                        return this.$t('sort_emails')
                }
            } else {
                return this.$t('sort_emails')
            }

        },

        isList() {
            return this.page === ENUM.NEWSLETTERS.PAGE.LIST && !this.showEmpty
        }
    },

    data() {
        return {
            searching: false,
            searchValue: ''
        }
    },

    methods: {

        ...Vuex.mapActions(['unselectedAllNewsletters']),

        updateNewslettersPage(newPage) {
            newPage === ENUM.NEWSLETTERS.PAGE.LIST
                ? Tag.clickGA(this, 'list')
                : Tag.clickGA(this, 'cards')

            this.$emit('updateNewslettersPage', newPage)
        },

        updateCategory(newCategory) {
            this.$emit('updateCategory', newCategory)
        },

        openSort() {
            Tag.clickGA(this, 'sort_emails')
            this.$refs.SORT.open()
        },

        goMyActions() {
            Storage.setItem(Storage.KEYS.NEWSLETTERS_PAGE, this.page)
            this.$router.push({path: '/my-actions'})
        },

        search() {
            this.isDesktop
                ? Tag.clickGA(this, 'search_cta')
                : Tag.clickGA(this, 'search_email')
            this.searching = true
        },

        openBSMS() {
            this.$refs.BSMS.open()
        }
    }
}
</script>

<style lang="scss" scoped>

.header {

    &__row {
        display: flex;
        flex-direction: row;
        flex-flow: row wrap;
        align-items: center;
        justify-content: space-between;
        padding: 8px 16px;
        background-color: $timberwolf;

        @media screen and (min-width: $above-tablet-length) {
            background-color: $white;
            border-bottom: 1px solid $divider;
        }
    }

    &__mobile {
        &__selected {
            display: flex;
            flex-direction: row;
            align-items: center;
            padding: 8px 16px 8px 12px;
            border-radius: 72px;
        }
    }

    &__progress {
        display: flex;
        flex-direction: row;

        &__right {
            text-align: left;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
        }
    }

    &__choice {
        border-radius: 72px;
        padding: 2px;
        background-color: $white;
        display: flex;
        flex-direction: row;
    }

    &__line {
        display: flex;
        flex-direction: row;
    }


    &__btn {
        &__mail {
            display: flex;
            flex-direction: row;
            padding: 8px 12px 8px 16px;
            align-items: center;
            border-radius: 72px;
            background-color: $white;
            color: $charcoal;
        }

        &__history {
            display: flex;
            flex-direction: row;
            padding: 8px 16px;
            align-items: center;
            background-color: $aquamarine;
            border-radius: 72px;
        }

        &__template {
            padding: 8px 16px;
            border-radius: 72px;
            color: $midnight;
            background-color: $white;

            &__active {
                background-color: $midnight;
                color: $white;
            }
        }

        &__search {
            display: flex;
            background-color: $white;
            align-items: center;
            justify-content: center;
            border-radius: 72px;
            padding: 12px;
        }

        &__sort {
            display: flex;
            flex-direction: row;
            align-items: center;
            background-color: $white;
            border-radius: 72px;
            padding: 8px 12px;
            @media screen and (min-width: $above-book-length) {
                border: solid 2px $silver;
            }

            &__active {
                border: solid 2px $aquamarine;
                color: $aquamarine;
            }
        }
    }

    &__search {
        display: flex;
        flex-direction: row;
        padding: 16px;
        background-color: $timberwolf;

        @media screen and (min-width: $above-tablet-length) {
            background-color: $white;
            justify-content: center;
            border-bottom: 1px solid $divider;
        }

        &__label {
            padding: 8px 16px;
            display: flex;
            flex: 1;
            flex-direction: row;
            align-items: center;
            border-radius: 72px;
            background-color: $white;
            max-width: 650px;
            @media screen and (min-width: $above-tablet-length) {
                border: 2px solid $silver;
            }
        }

        &__input {
            width: 100%;
        }

        &__input::placeholder {
            font-size: 17px;
            font-weight: 600;
            line-height: 1.41;
            color: $stone;
        }
    }
}
</style>
